function pickHeaderValue(hits, getters) {
  for (const getter of getters) {
    for (const hit of hits) {
      const value = getter(hit);

      if (value != null && value !== '') {
        return value;
      }
    }
  }

  return null;
}

function buildCaseInstancePath(hits) {
  const withController = hits.find((hit) => hit.instance_path?.controller_id != null);
  return withController?.instance_path ?? hits[0]?.instance_path ?? {};
}

function buildCaseHeader(hits) {
  return {
    organization_id: pickHeaderValue(hits, [
      (hit) => hit.instance_path?.organization_id,
      (hit) => hit.facts?.organization_id,
    ]),
    user_id: pickHeaderValue(hits, [(hit) => hit.instance_path?.user_id, (hit) => hit.facts?.user_id]),
    user_full_name: pickHeaderValue(hits, [(hit) => hit.facts?.user_full_name]),
    user_phone: pickHeaderValue(hits, [(hit) => hit.facts?.user_phone]),
    address: pickHeaderValue(hits, [(hit) => hit.facts?.address]),
    controller_serial: pickHeaderValue(hits, [
      (hit) => (hit.risk_number === 1 ? hit.facts?.serial_number : null),
      (hit) => hit.facts?.controller_serial_number,
      (hit) => hit.facts?.serial_number,
    ]),
    lk_client_available: hits.some((hit) => hit.facts?.lk_client_available === true),
  };
}

function scoreOrNull(value) {
  return value == null || !Number.isFinite(Number(value)) ? null : Number(value);
}

function computeCaseScore(hits) {
  const scores = hits.map((hit) => scoreOrNull(hit.rule_result?.risk_score)).filter((v) => v != null);
  if (!scores.length) return null;
  return Math.max(...scores);
}

function computeCaseDeadline(hits) {
  const deadlines = hits
    .map((hit) => hit.rule_result?.deadline_at)
    .filter(Boolean)
    .map((value) => new Date(value).getTime());

  if (deadlines.length === 0) {
    return null;
  }

  return new Date(Math.min(...deadlines)).toISOString();
}

export function caseKeyFromHit(hit) {
  const path = hit.instance_path || {};

  if (path.controller_id != null) {
    return `controller:${path.controller_id}`;
  }

  if (path.user_id != null) {
    return `user:${path.user_id}`;
  }

  const riskNumber = hit.risk_number ?? 'unknown';
  const entityId = hit.entity_id ?? hit.facts?.device_id ?? hit.facts?.counter_id ?? 'unknown';

  return `orphan:${riskNumber}:${entityId}`;
}

export function sortCases(cases) {
  return [...cases].sort((left, right) => {
    const leftScore = scoreOrNull(left.case_score);
    const rightScore = scoreOrNull(right.case_score);
    if (leftScore == null && rightScore != null) return 1;
    if (rightScore == null && leftScore != null) return -1;
    if (leftScore != null && rightScore != null && rightScore !== leftScore) {
      return rightScore - leftScore;
    }

    const leftDeadline = left.case_deadline ? new Date(left.case_deadline).getTime() : Number.POSITIVE_INFINITY;
    const rightDeadline = right.case_deadline ? new Date(right.case_deadline).getTime() : Number.POSITIVE_INFINITY;

    return leftDeadline - rightDeadline;
  });
}

export function paginateCases(cases, page, pageSize) {
  const safePage = Math.max(1, page);
  const safePageSize = Math.max(1, pageSize);
  const offset = (safePage - 1) * safePageSize;

  return cases.slice(offset, offset + safePageSize);
}

export function buildRiskCases(hits) {
  const grouped = new Map();

  for (const hit of hits) {
    const key = caseKeyFromHit(hit);

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }

    grouped.get(key).push(hit);
  }

  const cases = Array.from(grouped.entries()).map(([key, caseHits]) => ({
    key,
    instance_path: buildCaseInstancePath(caseHits),
    header: buildCaseHeader(caseHits),
    hits: caseHits,
    case_score: computeCaseScore(caseHits),
    case_deadline: computeCaseDeadline(caseHits),
  }));

  return sortCases(cases);
}

/**
 * Canon (KB mira-safe/риски-ui-таблица-и-мёртвые):
 * - table + multi-risk → one row per hit (own deadline/score), still expandable;
 * - cards → always one case per controller (risk tabs on the card). Never flatten cards.
 */
export function casesForDashboardView(groupedCases, { isMultiRisk = false, viewMode = 'table', riskNumbers = null } = {}) {
  if (isMultiRisk && viewMode === 'table') {
    return expandCasesToFilteredHitRows(groupedCases, riskNumbers);
  }
  return groupedCases;
}

/**
 * Multi-risk filter (table only): one row per selected hit (own deadline/score).
 * Keeps full case hits so the row can still expand to #1–#4.
 */
export function expandCasesToFilteredHitRows(cases, riskNumbers = null) {
  const preferred = (Array.isArray(riskNumbers) ? riskNumbers : [])
    .map(Number)
    .filter(Number.isFinite);
  const rows = [];

  for (const riskCase of cases || []) {
    const allHits = riskCase.hits || [];
    let rowHits = allHits;
    if (preferred.length > 0) {
      rowHits = allHits.filter((hit) => preferred.includes(Number(hit.risk_number)));
    }

    for (const hit of rowHits) {
      const riskNumber = hit.risk_number ?? 'unknown';
      const entityId = hit.entity_id ?? hit.facts?.device_id ?? hit.facts?.counter_id ?? '';
      rows.push({
        key: `${riskCase.key}:r${riskNumber}:${entityId}`,
        instance_path: riskCase.instance_path,
        header: riskCase.header,
        hits: allHits,
        summaryRiskNumber: Number(hit.risk_number),
        case_score: scoreOrNull(hit.rule_result?.risk_score),
        case_deadline: hit.rule_result?.deadline_at ?? null,
      });
    }
  }

  return sortCases(rows);
}
